import React, { useState } from 'react';
import { Avatar, Button } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { storageImage } from '../../shared/utils/storage-image';

type Props = {
  // Bu hafta katılan ve izleyicinin henüz hoş geldin demediği oyuncuların katılım olayları.
  events: LeagueFeed.Event[];
  // Tek tek hoş geldin (mevcut tebrik API'si); sunucu onayıyla true döner.
  // eslint-disable-next-line no-unused-vars -- callback type signature
  onWelcome: (event: LeagueFeed.Event) => Promise<boolean>;
};

const initials = (player?: LeagueFeed.Player) =>
  [player?.ad, player?.soyad]
    .filter(Boolean)
    .map(part => part!.slice(0, 1).toLocaleUpperCase('tr-TR'))
    .join('') || '—';

/**
 * LG-65: yeni oyuncuya ilk karşılık. Bu haftanın katılımları tek satırda; tek dokunuşla hepsine
 * hoş geldin (👏). Aynı gün 3+ katılım toplu kartta tebriksiz kaldığı için o oyuncular da buradan
 * karşılanır. Başarı yalnız sunucu onayından sonra gösterilir.
 */
export default function FeedWelcome({ events, onWelcome }: Props) {
  const { t } = useTranslation('leagues');
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  // Gönderim başlayınca liste sabitlenir (hoş geldin denenler üst listeden düşer).
  const [sent, setSent] = useState<LeagueFeed.Event[]>([]);
  const shown = status === 'idle' ? events : sent;
  if (!shown.length) return null;
  const players = shown.map(event => event.veri.oyuncu).filter(Boolean) as LeagueFeed.Player[];

  const welcomeAll = async () => {
    setSent(events);
    setStatus('sending');
    const results = [];
    // Sırayla: tebrik API'sindeki kilit ve sayaçlar olay başına çalışır.
    // eslint-disable-next-line no-restricted-syntax -- sıralı istek bilerek
    for (const event of events) results.push(await onWelcome(event)); // eslint-disable-line no-await-in-loop
    setStatus(results.every(Boolean) ? 'done' : 'error');
  };

  return (
    <section className="feed-welcome" aria-labelledby="feed-welcome-title">
      <div className="feed-welcome__faces" aria-hidden="true">
        {players.slice(0, 5).map(player => (
          <Avatar key={player.id} size={32} src={storageImage(player.foto_mini_url, 32)} alt="">
            {initials(player)}
          </Avatar>
        ))}
      </div>
      <p id="feed-welcome-title" className="feed-welcome__text">
        {t('detail.feed.welcomeTitle', { count: shown.length })}
      </p>
      {status === 'done' ? (
        <p className="feed-welcome__done" role="status">
          <CheckOutlined aria-hidden="true" /> {t('detail.feed.welcomeDone')}
        </p>
      ) : (
        <Button className="feed-welcome__action" onClick={welcomeAll} loading={status === 'sending'}>
          {t('detail.feed.welcome')}
        </Button>
      )}
      {status === 'error' && (
        <p className="feed-welcome__error" role="alert">
          {t('detail.feed.congratsError')}
        </p>
      )}
    </section>
  );
}
